import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SortType, SelectPicker } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { deleteClubTag, getPagedClubTags } from '@/services/club-tag.service';
import { ClubTag, ClubTagPaginationFilterRequest, ClubTagTypeList, clubTagTypeLabel } from '@/models/club-tag.model';
import EditIcon from '@rsuite/icons/Edit';
import TrashIcon from '@rsuite/icons/Trash';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ClubTagDrawer from './club-tag-drawer';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const ClubTagList = () => {
  const [filterReq, setFilterReq] = useState<ClubTagPaginationFilterRequest>({
    ...new ClubTagPaginationFilterRequest(),
    pageSize: 100,
    orderBy: 'Type',
    isAscending: true,
  });
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<ClubTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [idToEdit, setIdToEdit] = useState<number | undefined>(undefined);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: ClubTagPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<ClubTag> = await getPagedClubTags(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching club tags:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setIdToEdit(undefined);
    fetchData(filterReq);
  };

  const onEditItem = (id: number) => {
    setIdToEdit(id);
    setOpenDrawer(true);
  };

  const onDeleteItem = async (tag: ClubTag) => {
    if (!window.confirm(`Delete tag "${tag.title}"? It will also be removed from every club that has it.`)) {
      return;
    }
    try {
      await deleteClubTag(tag.id);
      fetchData(filterReq);
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in deleting club tag:${errorModel?.errorMessage}`);
    }
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => setOpenDrawer(true)}>
          Add Tag
        </Button>

        <Stack spacing={6}>
          <SelectPicker
            placeholder="Type"
            data={ClubTagTypeList}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={value => setFilterReq(pre => ({ ...pre, type: value ?? undefined, pageNumber: 1 }))}
          />
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey, pageNumber: 1 }))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column width={80} align="left" sortable>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={1} align="left" sortable>
          <HeaderCell>Type</HeaderCell>
          <Cell dataKey="type">
            {rowData => clubTagTypeLabel(rowData.type)}
          </Cell>
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Key</HeaderCell>
          <Cell dataKey="key" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Title</HeaderCell>
          <Cell dataKey="title" />
        </Column>
        <Column width={100} align="left" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (
              <Stack spacing={4}>
                <IconButton onClick={() => onEditItem(rowData.id)} icon={<EditIcon />} circle size="sm" />
                <IconButton onClick={() => onDeleteItem(rowData as ClubTag)} icon={<TrashIcon />} color="red" appearance="subtle" circle size="sm" />
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />

      <ClubTagDrawer
        clubTagId={idToEdit}
        open={openDrawer}
        onClose={handleCloseDrawer}
      />
    </PageContent>
  );
};

export default ClubTagList;
